/**
 * Bundle Upsell — per-accessory color picker, dynamic hint, summary,
 * and Add-to-cart hijack to add main bag + selected accessories together.
 *
 * Reads config from <script type="application/json" data-bundle-config>.
 * Plays nice with Dawn's product-form.js (PUB_SUB_EVENTS) and cart-drawer.
 *
 * ============================================================================
 * BUILD-MARKER-2026-06-04-combo-v4-cart-update-discount-codes-array
 * Combo mode uses Cart Ajax /cart/update.js discount_codes ARRAY (atomic).
 * Replaces sequential /discount/X calls which raced and only kept last code.
 * If you see "combo codes applied:" (plural with 's') log this is v4.
 * If you see "combo code applied:" (singular) it's stale v3, hard-refresh.
 * ============================================================================
 */
console.log('[bundle-upsell] LOADED v4-cart-update-discount-codes-array');

if (!customElements.get('bundle-upsell')) {
  customElements.define(
    'bundle-upsell',
    class BundleUpsell extends HTMLElement {
      connectedCallback() {
        console.log('[bundle-upsell] connected. JS-build=2026-06-09-netprice-v18-untagged-atomic');
        const configEl = this.querySelector('[data-bundle-config]');
        if (!configEl) {
          console.warn('[bundle-upsell] no config element found');
          return;
        }

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (err) {
          console.error('[bundle-upsell] config parse error', err);
          return;
        }
        console.log('[bundle-upsell] config loaded:', {
          isComboMode: this.config.isComboMode,
          enabled: this.config.enabled,
          overrideCode: this.config.overrideCode,
          useAutomaticDiscount: this.config.useAutomaticDiscount,
        });

        this.hintEl = this.querySelector('[data-bundle-hint]');
        this.summaryEl = this.querySelector('[data-summary]');
        this.subtotalEl = this.querySelector('[data-subtotal]');
        this.discountEl = this.querySelector('[data-discount]');
        this.totalEl = this.querySelector('[data-total]');
        this.picks = Array.from(this.querySelectorAll('.bundle-pick'));

        // Combo Set mode: each pick carries data-combo-role + data-combo-savings-cents.
        // currentIsAnchor = no pick on page has role=anchor → current product IS the anchor.
        // Savings only fire when anchor is in cart (current=anchor OR anchor pick selected);
        // Shopify Automatic discounts (Trio-Pod / Trio-Charm etc) apply at checkout.
        this.isComboMode = !!this.config.isComboMode;
        if (this.isComboMode) {
          this.currentIsAnchor = !this.picks.some((p) => p.dataset.comboRole === 'anchor');
          // Debug: confirm combo data attrs are present on each pick
          console.log('[bundle-upsell] combo mode picks:', this.picks.map((p) => ({
            id: p.dataset.accessoryId,
            role: p.dataset.comboRole,
            savings: p.dataset.comboSavingsCents,
            code: p.dataset.comboCode || '(none)',
          })), 'currentIsAnchor=', this.currentIsAnchor);
        }

        this.selections = {}; // accessoryId -> { variantId, price, colorName }

        this.picks.forEach((pick) => this.bindPick(pick));

        // Lightbox
        this.lightbox = this.querySelector('[data-bundle-lightbox]');
        this.lightboxImg = this.querySelector('[data-bundle-lightbox-img]');
        this.bindLightbox();

        // Hijack the product form's submit so adding to cart includes accessories.
        const formId = this.dataset.productFormId;
        if (formId) {
          this.productForm = document.getElementById(formId);
          if (this.productForm) {
            this.productForm.addEventListener('submit', (e) => this.handleFormSubmit(e), true);
          }
        }

        // Watch main bag variant changes
        this.watchMainVariant();

        // #2 fix: if the anchor was added on an earlier page, the combo is already active here.
        // POST /cart/update.js (empty) returns the full cart and isn't blocked by the preview shim.
        this.cartHasComboAnchor = false;
        if (
          this.isComboMode &&
          this.config.currentIsCompanion &&
          Array.isArray(this.config.comboAnchorIds) &&
          this.config.comboAnchorIds.length
        ) {
          fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
            .then((r) => r.json())
            .then((cart) => {
              this.cartHasComboAnchor = (cart.items || []).some(
                (i) => this.config.comboAnchorIds.indexOf(i.product_id) !== -1
              );
              if (this.cartHasComboAnchor) this.update();
            })
            .catch(() => {});
        }

        this.update();
      }

      bindLightbox() {
        if (!this.lightbox) return;

        // Move lightbox to <body> so position:fixed escapes any sticky/transformed parent.
        if (this.lightbox.parentNode !== document.body) {
          document.body.appendChild(this.lightbox);
        }
        // Re-query img reference in case it was relocated.
        this.lightboxImg = this.lightbox.querySelector('[data-bundle-lightbox-img]');

        const triggers = this.querySelectorAll('[data-bundle-lightbox-trigger]');
        const closeBtn = this.lightbox.querySelector('[data-bundle-lightbox-close]');

        triggers.forEach((t) => {
          t.addEventListener('click', (e) => {
            e.preventDefault();
            // Prefer current data-image-large (may be updated to variant image)
            const url = t.dataset.imageLarge || t.dataset.imageDefaultLarge;
            const alt = t.dataset.imageAlt || '';
            if (!url) return;
            this.openLightbox(url, alt);
          });
        });

        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeLightbox());
        }
        // Click outside image to close
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox) this.closeLightbox();
        });
        // Esc to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.lightbox && !this.lightbox.hidden) this.closeLightbox();
        });
      }

      openLightbox(url, alt) {
        if (!this.lightbox || !this.lightboxImg) return;
        this.lightboxImg.src = url;
        this.lightboxImg.alt = alt;
        this.lightbox.hidden = false;
        // Force reflow so the transition runs
        void this.lightbox.offsetHeight;
        this.lightbox.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.classList.remove('is-open');
        document.body.style.overflow = '';
        // Wait for fade-out before hiding
        setTimeout(() => {
          if (this.lightbox && !this.lightbox.classList.contains('is-open')) {
            this.lightbox.hidden = true;
          }
        }, 220);
      }

      bindPick(pickEl) {
        const accId = pickEl.dataset.accessoryId;
        const swatchesWrap = pickEl.querySelector('.bundle-pick__swatches');
        const isMultiOption = swatchesWrap && swatchesWrap.dataset.multiOption === 'true';
        const swatches = pickEl.querySelectorAll('.bundle-swatch');
        const removeBtn = pickEl.querySelector('[data-remove]');

        // For multi-option accessories (e.g., shoes with Color + Size), parse the
        // variants JSON so we can resolve a (color, size) pair to a specific variant.
        let variantsForPick = null;
        if (isMultiOption) {
          const variantsScript = pickEl.querySelector('[data-pick-variants]');
          if (variantsScript) {
            try {
              variantsForPick = JSON.parse(variantsScript.textContent);
            } catch (e) {
              console.warn('[bundle-upsell] failed to parse variants JSON for', accId, e);
            }
          }
          // Bind size pills ONCE (rendered upfront in Liquid)
          const sizePills = pickEl.querySelectorAll('[data-size-pills] .bundle-size-pill');
          sizePills.forEach((pill) => {
            pill.addEventListener('click', (e) => {
              e.preventDefault();
              if (pill.disabled) return;
              const sizeName = pill.dataset.size || '';
              this.onMultiOptionSizePick(pickEl, accId, sizeName, variantsForPick);
            });
          });
        }

        swatches.forEach((sw) => {
          sw.addEventListener('click', (e) => {
            e.preventDefault();
            if (sw.disabled) return;

            const colorName = sw.dataset.colorName || '';
            const variantId = parseInt(sw.dataset.variantId, 10);

            if (isMultiOption && variantsForPick) {
              this.onMultiOptionColorPick(pickEl, accId, colorName, variantsForPick);
              return;
            }

            // Single-option behavior (legacy): color click directly selects variant
            const price = parseInt(sw.dataset.variantPrice, 10);
            // Net-price companion swatches carry compareAt (= original price) for the strike row.
            const compareAt = parseInt(sw.dataset.variantCompare, 10) || price;
            const variantImage = sw.dataset.variantImage || '';
            const variantImageLarge = sw.dataset.variantImageLarge || '';
            const wasSelectedSame =
              this.selections[accId] && this.selections[accId].variantId === variantId;

            if (wasSelectedSame) {
              this.deselectAccessory(accId);
            } else {
              this.selectAccessory(accId, {
                variantId,
                price,
                compareAt,
                colorName,
                variantImage,
                variantImageLarge,
              });
            }
          });
        });

        if (removeBtn) {
          removeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.deselectAccessory(accId);
            if (isMultiOption) {
              if (this.pendingColor) delete this.pendingColor[accId];
              if (this.pendingSize) delete this.pendingSize[accId];
              this.update();
            }
          });
        }
      }

      // ===== Multi-option flow (Color + Size, INDEPENDENT states) =====
      // pendingColor[accId] and pendingSize[accId] are independent. Each click toggles
      // its own dimension. When both are set AND a matching available variant exists,
      // selectAccessory commits that variant. Switching one side preserves the other.
      onMultiOptionColorPick(pickEl, accId, colorName, variants) {
        this.pendingColor = this.pendingColor || {};
        // Toggle off if clicking the same color
        if (this.pendingColor[accId] === colorName) {
          delete this.pendingColor[accId];
        } else {
          this.pendingColor[accId] = colorName;
        }
        this.tryFinalizeMultiOption(accId, variants);
      }

      onMultiOptionSizePick(pickEl, accId, sizeName, variants) {
        this.pendingSize = this.pendingSize || {};
        if (this.pendingSize[accId] === sizeName) {
          delete this.pendingSize[accId];
        } else {
          this.pendingSize[accId] = sizeName;
        }
        this.tryFinalizeMultiOption(accId, variants);
      }

      tryFinalizeMultiOption(accId, variants) {
        const c = this.pendingColor && this.pendingColor[accId];
        const s = this.pendingSize && this.pendingSize[accId];
        if (c && s && variants) {
          const v = variants.find((x) => x.color === c && x.size === s);
          if (v && v.available) {
            // Both picked + valid combo → commit variant
            this.selectAccessory(accId, {
              variantId: v.id,
              price: v.price,
              compareAt: v.compareAt || v.price,
              colorName: v.color + ' / ' + v.size,
              variantImage: v.image || '',
              variantImageLarge: v.imageLarge || '',
            });
            return;
          }
        }
        // Either not both picked, or combo unavailable — clear selection but keep pendings as visual marks
        if (this.selections[accId]) {
          this.deselectAccessory(accId); // calls update()
        } else {
          this.update();
        }
      }

      updatePickImage(pickEl, variantImage, variantImageLarge) {
        const imgEl = pickEl.querySelector('.bundle-pick__image');
        const imgBtn = pickEl.querySelector('.bundle-pick__image-btn');
        if (!imgBtn) return;

        const defaultImage = imgBtn.dataset.imageDefault || '';
        const defaultLarge = imgBtn.dataset.imageDefaultLarge || '';

        const target = variantImage || defaultImage;
        const targetLarge = variantImageLarge || defaultLarge;

        if (imgEl && target) {
          imgEl.src = target;
          // Drop srcset so the explicit src wins
          imgEl.removeAttribute('srcset');
        }
        if (targetLarge) {
          imgBtn.dataset.imageLarge = targetLarge;
        }
      }

      selectAccessory(accId, payload) {
        // Multi-anchor combo: anchors within the SAME combo behave as radio buttons.
        // When customer picks an anchor (e.g. Solé Tote) for Sunshine Combo, any
        // previously-selected anchor in that same combo (e.g. Paws & Go Tote) is
        // auto-deselected. Companions are unaffected. Different combos don't interfere.
        const pickEl = this.picks.find((p) => p.dataset.accessoryId === accId);
        if (pickEl && pickEl.dataset.comboRole === 'anchor') {
          const comboHandle = pickEl.dataset.comboHandle || '';
          if (comboHandle) {
            this.picks.forEach((other) => {
              if (other === pickEl) return;
              if (other.dataset.comboRole !== 'anchor') return;
              if ((other.dataset.comboHandle || '') !== comboHandle) return;
              const otherAccId = other.dataset.accessoryId;
              if (this.selections[otherAccId]) delete this.selections[otherAccId];
              // Also clear any pending multi-option state on the swapped-out anchor
              if (this.pendingColor) delete this.pendingColor[otherAccId];
              if (this.pendingSize) delete this.pendingSize[otherAccId];
            });
          }
        }
        this.selections[accId] = payload;
        this.update();
      }

      deselectAccessory(accId) {
        delete this.selections[accId];
        this.update();
      }

      watchMainVariant() {
        // Dawn updates a script[data-selected-variant] with the current variant on swatch change.
        // Observe it and refresh main price.
        const variantScript = document.querySelector('script[data-selected-variant]');
        if (!variantScript) return;

        const refresh = () => {
          try {
            const v = JSON.parse(variantScript.textContent);
            if (v && v.id) {
              this.config.mainVariantId = v.id;
              this.config.mainPrice = v.price;
              this.update();
            }
          } catch (e) {}
        };

        const observer = new MutationObserver(refresh);
        observer.observe(variantScript, { childList: true, characterData: true, subtree: true });
        this._variantObserver = observer;
      }

      // ===== Pricing =====
      isAnchorInCart() {
        // Combo mode helper: anchor is "in cart" if current product is anchor OR
        // user has selected the anchor pick (when current is a companion page).
        if (!this.isComboMode) return false;
        if (this.currentIsAnchor) return true;
        // #2 fix: anchor added on an earlier page (already sitting in the cart) also counts.
        if (this.cartHasComboAnchor) return true;
        return Object.keys(this.selections).some((accId) => {
          const pick = this.picks.find((p) => p.dataset.accessoryId === accId);
          return pick && pick.dataset.comboRole === 'anchor';
        });
      }

      computeTotals() {
        const selectedList = Object.values(this.selections);
        const accessoryTotal = selectedList.reduce((s, x) => s + (x.price || 0), 0);
        const count = selectedList.length;

        // Combo mode (net-price architecture): each companion pick already carries the
        // UNLISTED net-price variant (e.g. $20 Pod), so its `price` IS the discounted price
        // and `compareAt` is the original. No discount engine — the price is the discount.
        if (this.isComboMode) {
          const accOrig = selectedList.reduce((s, x) => s + (x.compareAt || x.price || 0), 0);
          let mainNet = parseInt(this.config.mainPrice, 10) || 0;
          let mainOrig = mainNet;
          // Companion page: when the anchor is chosen, the full-price companion the customer
          // is viewing (e.g. $58 Pod) gets swapped to its net variant ($20) at add-time.
          // Reflect that swap in the live summary so the preview total matches the cart.
          if (this.config.currentIsCompanion && this.isAnchorInCart()) {
            const net = this.config.mainNetMap && this.config.mainNetMap[this.config.mainVariantId];
            if (net) { mainNet = net.price; mainOrig = net.compareAt || net.price; }
          }
          const total = mainNet + accessoryTotal; // accessoryTotal = sum of net prices
          const subtotal = mainOrig + accOrig; // original prices, drives the strike-through row
          const discount = Math.max(subtotal - total, 0);
          return { subtotal, discount, total, count, accessoryTotal };
        }

        let discount = 0;
        const isCampaign =
          this.config.enabled !== false &&
          (!!this.config.overrideCode || !!this.config.useAutomaticDiscount);

        if (isCampaign && count > 0) {
          // Campaign mode: flat per-item amount × count of selected accessories
          const perItem = parseInt(this.config.overrideAmountPerItem, 10) || 0;
          discount = perItem * count;
        } else if (this.config.enabled !== false && count > 0 && this.config.tiers) {
          const sorted = this.config.tiers.slice().sort((a, b) => b.qty - a.qty);
          const tier = sorted.find((t) => count >= t.qty);
          if (tier) {
            if (this.config.discountType === 'percentage') {
              discount = Math.round((accessoryTotal * tier.value) / 100);
            } else {
              discount = Math.round(tier.value * 100);
            }
          }
        }

        // Don't let discount exceed accessory total (defensive — Shopify wouldn't allow it either)
        discount = Math.min(discount, accessoryTotal);

        const mainPrice = parseInt(this.config.mainPrice, 10) || 0;
        const subtotal = mainPrice + accessoryTotal;
        const total = Math.max(subtotal - discount, 0);
        return { subtotal, discount, total, count, accessoryTotal };
      }

      // ===== Hint text =====
      buildTierText(tier, accessoryTotalIfChosen) {
        const labels = this.config.labels || {};
        const isPct = this.config.discountType === 'percentage';
        let savings = 0;
        if (isPct) {
          savings = Math.round((accessoryTotalIfChosen * tier.value) / 100);
        } else {
          savings = Math.round(tier.value * 100);
        }

        const savingsStr = this.formatMoney(savings);

        if (isPct) {
          const discountPct = tier.value;
          const discountZh = ((100 - tier.value) / 10).toFixed(1).replace(/\.0$/, '');
          const tpl = labels.tierTextPercentage || '[discount_pct]% off, save [savings]';
          return tpl
            .replace('[discount_pct]', discountPct)
            .replace('[discount_zh]', discountZh)
            .replace('[discount]', discountZh)
            .replace('[savings]', savingsStr);
        }
        const tpl = labels.tierTextAmount || '[savings] off';
        return tpl.replace('[savings]', savingsStr);
      }

      buildHint(count, accessoryTotal) {
        const labels = this.config.labels || {};

        // Bundle discount paused → suppress all tier-related text, show empty hint.
        if (this.config.enabled === false) {
          return '';
        }

        // Combo mode: no tier ladder hint. Subtitle already explains the offer.
        if (this.isComboMode) {
          return '';
        }

        const tiers = this.config.tiers || [];
        const sorted = tiers.slice().sort((a, b) => a.qty - b.qty);
        const maxTier = sorted[sorted.length - 1];

        if (maxTier && count >= maxTier.qty) {
          return labels.hintMaxReached || 'Maximum bundle discount unlocked';
        }

        const nextTier = sorted.find((t) => t.qty > count);
        if (!nextTier) {
          return labels.hintDefault || 'Add accessories to unlock bundle savings';
        }

        const remaining = nextTier.qty - count;
        const avgPerAccessory = count > 0 ? accessoryTotal / count : this.estimateAvgAccessoryPrice();
        const projectedAccTotal = avgPerAccessory * nextTier.qty;
        const tierText = this.buildTierText(nextTier, projectedAccTotal);

        const tierBadge = (labels.tierBadgeWith || '+[count] · [tierText]')
          .replace('[count]', nextTier.qty)
          .replace('[tierText]', tierText);

        const badgeHtml = `<span class="save-hint">${this.escapeHtml(tierBadge)}</span>`;

        if (count === 0) {
          const base = labels.hintDefault || 'Add accessories to unlock bundle savings';
          return `${this.escapeHtml(base)} ${badgeHtml}`;
        }

        const tpl = labels.hintNextTier || 'Add [remaining] more for [nextTierText]';
        return tpl
          .replace('[remaining]', remaining)
          .replace('[nextTierText]', `<span class="save-hint">${this.escapeHtml(tierText)}</span>`);
      }

      estimateAvgAccessoryPrice() {
        // Read all swatch prices to get the accessory pricing baseline.
        const prices = [];
        this.picks.forEach((pick) => {
          const sw = pick.querySelector('.bundle-swatch');
          if (sw && sw.dataset.variantPrice) {
            prices.push(parseInt(sw.dataset.variantPrice, 10));
          }
        });
        if (prices.length === 0) return 0;
        return prices.reduce((s, p) => s + p, 0) / prices.length;
      }

      // ===== Render =====
      update() {
        const { subtotal, discount, total, count, accessoryTotal } = this.computeTotals();
        const activePct = this.currentTierPct(count);
        // Per-item savings in cents. Campaign mode (code or automatic): fixed amount from metafield.
        // Normal mode: % off applied per accessory price (computed inline below).
        const isCampaign =
          this.config.enabled !== false &&
          (!!this.config.overrideCode || !!this.config.useAutomaticDiscount);
        const campaignAmount = isCampaign ? (parseInt(this.config.overrideAmountPerItem, 10) || 0) : 0;

        // Update stepper
        this.updateStepper(count);

        // Update hint
        if (this.hintEl) {
          this.hintEl.innerHTML = this.buildHint(count, accessoryTotal);
        }

        // Update each pick visual
        this.picks.forEach((pick) => {
          const accId = pick.dataset.accessoryId;
          const sel = this.selections[accId];
          const colorNameEl = pick.querySelector('[data-color-name]');
          const removeBtn = pick.querySelector('[data-remove]');
          const priceEl = pick.querySelector('[data-pick-price]');
          const swatches = pick.querySelectorAll('.bundle-swatch');

          // Combo mode: hide entire COMPANION pick block until an anchor is in cart.
          // Progressive disclosure — customer sees only the anchor pick first ("Add
          // Phone Pouch"), picks it, then companion picks appear ("Add Charm").
          // Prevents the confusing state where Charm shows "Save HK$31" but won't
          // actually fire savings without anchor. Anchor picks stay visible.
          if (this.isComboMode) {
            const role = pick.dataset.comboRole;
            if (role === 'companion') {
              pick.classList.toggle('bundle-pick--locked', !this.isAnchorInCart());
            } else {
              pick.classList.remove('bundle-pick--locked');
            }
          }

          // Mark currently-chosen swatch + size pill.
          // For multi-option mode: ONLY look at pendingColor / pendingSize (independent states).
          //   - Color swatch with name === pendingColor → is-selected
          //   - Size pill with name === pendingSize → is-selected
          // For single-option mode: legacy is-selected based on selected variant id.
          const multiOption = pick.querySelector('.bundle-pick__swatches[data-multi-option="true"]') !== null;
          if (multiOption) {
            const pColor = this.pendingColor && this.pendingColor[accId];
            const pSize = this.pendingSize && this.pendingSize[accId];
            swatches.forEach((sw) => {
              sw.classList.remove('is-pending');
              sw.classList.toggle('is-selected', !!pColor && sw.dataset.colorName === pColor);
            });
            pick.querySelectorAll('[data-size-pills] .bundle-size-pill').forEach((p) => {
              p.classList.toggle('is-selected', !!pSize && p.dataset.size === pSize);
            });
            const sizeNameEl = pick.querySelector('[data-size-name]');
            if (sizeNameEl) {
              const placeholder = (this.config.labels && this.config.labels.choosePlaceholder) || 'Choose';
              sizeNameEl.textContent = pSize || placeholder;
            }
          } else {
            swatches.forEach((sw) => {
              sw.classList.remove('is-pending');
              const isSel = sel && parseInt(sw.dataset.variantId, 10) === sel.variantId;
              sw.classList.toggle('is-selected', !!isSel);
            });
          }

          if (sel) {
            pick.classList.remove('is-dimmed');
            pick.classList.add('is-selected');
            if (colorNameEl) colorNameEl.textContent = sel.colorName || '';
            if (removeBtn) removeBtn.hidden = false;
            this.updatePickImage(pick, sel.variantImage, sel.variantImageLarge);
            // savings display per pick:
            //   - Combo mode: pre-rendered static pill badge in Liquid (.bundle-pick__combo-save).
            //     JS only updates the strike+discounted price; inline "Save" text suppressed.
            //   - Tier / Campaign mode: dynamic pill badge populated by JS into the
            //     [data-bundle-savings-pill] placeholder. Same visual style as combo.
            //     Inline "Save" text inside price also suppressed (avoid double-show).
            let perItemSavings;
            let pickOriginal = sel.price;
            if (this.isComboMode) {
              if (pick.dataset.comboRole === 'anchor') {
                // Mirror live: show the companion's discount ON the anchor pick (visual only —
                // in cart the discount lands on the companion via the net swap, so the anchor +
                // companion visual total still equals the real combo total). Anchor stays
                // full price in cart; here it just renders struck to communicate the combo.
                perItemSavings = this.isAnchorInCart() ? (parseInt(pick.dataset.comboSavingsCents, 10) || 0) : 0;
                pickOriginal = sel.price;
              } else {
                // Companion: net-price variant — strike its own compareAt down to net price.
                pickOriginal = sel.compareAt || sel.price;
                perItemSavings = pickOriginal - sel.price;
              }
            } else if (isCampaign) {
              perItemSavings = campaignAmount;
            } else {
              perItemSavings = Math.round((sel.price * activePct) / 100);
            }
            this.renderPickPrice(priceEl, pickOriginal, perItemSavings, { skipSavingLine: true });
            // Tier/Campaign modes: dynamically populate the dyn savings pill (combo
            // mode has its own static Liquid-rendered pill; don't touch that).
            if (!this.isComboMode) {
              const pill = pick.querySelector('[data-bundle-savings-pill]');
              if (pill) {
                if (perItemSavings > 0) {
                  const labels = this.config.labels || {};
                  const saveTpl = labels.savingsLabel || 'Save [amount]';
                  pill.textContent = saveTpl.replace('[amount]', this.formatMoney(perItemSavings));
                  pill.hidden = false;
                } else {
                  pill.hidden = true;
                }
              }
            }
          } else {
            pick.classList.add('is-dimmed');
            pick.classList.remove('is-selected');
            if (colorNameEl) {
              const placeholder = (this.config.labels && this.config.labels.choosePlaceholder) || 'Choose';
              // Multi-option: show pending color if user has picked one (even without size yet)
              const pColor = this.pendingColor && this.pendingColor[accId];
              colorNameEl.textContent = pColor || placeholder;
            }
            if (removeBtn) removeBtn.hidden = true;
            // Revert to default image
            this.updatePickImage(pick, '', '');
            this.resetPickPrice(priceEl);
            // Hide tier/campaign mode savings pill on deselect (combo pill stays — it's static)
            if (!this.isComboMode) {
              const pill = pick.querySelector('[data-bundle-savings-pill]');
              if (pill) pill.hidden = true;
            }
          }
        });

        // Default state: all unselected = dimmed; we still want the first-load look "neutral"
        // The class .is-dimmed handles it. (no-op)

        // Update summary
        if (count > 0) {
          this.summaryEl.hidden = false;
          this.subtotalEl.textContent = this.formatMoney(subtotal);
          this.discountEl.textContent = discount > 0 ? `-${this.formatMoney(discount)}` : this.formatMoney(0);
          this.totalEl.textContent = this.formatMoney(total);

          const discountRow = this.discountEl.closest('.bundle-summary-row--discount');
          if (discountRow) discountRow.style.display = discount > 0 ? '' : 'none';
        } else {
          this.summaryEl.hidden = true;
        }
      }

      // All variant ids equivalent to `vid` for combo dedupe: itself + its net/public
      // counterpart across every combo in the global registry (same source combo-cart uses).
      // Lets the add path treat a public variant and its net twin as "the same item in cart".
      comboEquivVariants(vid) {
        const out = [vid];
        const reg = Array.isArray(window.HULU_COMBO_REGISTRY) ? window.HULU_COMBO_REGISTRY : [];
        reg.forEach((combo) => (combo.companions || []).forEach((c) => {
          const n2p = c.netToPublic || {};
          const p2n = c.publicToNet || {};
          if (n2p[vid] != null && out.indexOf(Number(n2p[vid])) === -1) out.push(Number(n2p[vid]));
          if (p2n[vid] != null && out.indexOf(Number(p2n[vid])) === -1) out.push(Number(p2n[vid]));
        }));
        return out;
      }

      // ===== Add to cart hijack =====
      async handleFormSubmit(evt) {
        const selectedList = Object.values(this.selections);
        if (selectedList.length === 0) {
          // No bundle selections — let the normal form do its thing
          return;
        }

        // Take over: prevent default + add main + accessories together
        evt.preventDefault();
        evt.stopImmediatePropagation();

        const formData = new FormData(this.productForm);
        const mainVariantId = parseInt(formData.get('id'), 10) || this.config.mainVariantId;
        const mainQty = parseInt(formData.get('quantity'), 10) || 1;

        // ===== V2: combo mode goes through the single-writer orchestrator =====
        // Build line items tagged with _combo (companions + swapped main = net + _combo;
        // anchor = untagged full price). HuluComboCart does add + idempotent reconcile + repaint
        // atomically inside its mutex, so no cross-writer race and the cart ends up correct.
        if (this.isComboMode && window.HuluComboCart) {
          let mainComboId = '';
          Object.keys(this.selections).forEach((accId) => {
            const p = this.picks.find((x) => x.dataset.accessoryId === accId);
            if (p && p.dataset.comboRole === 'anchor') mainComboId = p.dataset.comboHandle || mainComboId;
          });

          // Read the current cart so we NEVER duplicate a line that's already there. The
          // orchestrator (reconcile) upgrades/downgrades existing companion lines by itself, so
          // re-adding a companion/anchor the customer already has would spawn a phantom 2nd line
          // (the root cause of "Astra becomes 2"). inCart(vid) is TRUE if vid OR its net/public
          // counterpart is already in cart — so a pick (net variant) is caught even when the
          // customer is holding the public variant, and vice-versa.
          const self = this;
          let inCart = function () { return false; };
          try {
            const c = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then((r) => r.json());
            const ids = new Set((c.items || []).map((i) => i.variant_id));
            inCart = function (vid) {
              const eq = self.comboEquivVariants(vid);
              for (let k = 0; k < eq.length; k++) if (ids.has(eq[k])) return true;
              return false;
            };
          } catch (_) {}

          const items = [];
          // Main product (the page the customer is on). Skip if it (public OR net) is already in cart.
          if (this.config.currentIsCompanion) {
            if (!inCart(mainVariantId)) {
              const mainNet = this.config.mainNetMap && this.config.mainNetMap[mainVariantId];
              if (this.isAnchorInCart() && mainNet && mainNet.id) items.push({ id: mainNet.id, quantity: mainQty });
              else items.push({ id: mainVariantId, quantity: mainQty });
            }
          } else {
            if (!inCart(mainVariantId)) items.push({ id: mainVariantId, quantity: mainQty }); // anchor-page main
          }
          // Selected picks (companions + anchor). Skip any variant already in cart → reconcile owns qty.
          Object.keys(this.selections).forEach((accId) => {
            const s = this.selections[accId];
            const p = this.picks.find((x) => x.dataset.accessoryId === accId);
            const role = p ? p.dataset.comboRole : 'companion';
            const cid = p ? (p.dataset.comboHandle || '') : '';
            if (inCart(s.variantId)) return;
            items.push({ id: s.variantId, quantity: 1 }); // untagged — reconcile owns net/public by variant
          });

          const submitBtn = this.productForm.querySelector('[type="submit"]');
          if (submitBtn) { submitBtn.classList.add('loading'); submitBtn.setAttribute('aria-disabled', 'true'); const sp = submitBtn.querySelector('.loading__spinner'); if (sp) sp.classList.remove('hidden'); }
          try {
            // Everything picked is already in cart → nothing new to add; just reconcile to fix prices.
            if (items.length) await window.HuluComboCart.addBundleIntent(items);
            else await window.HuluComboCart.reconcile();
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') drawer.open();
            this.selections = {};
            this.update();
          } catch (e) {
            console.error('[bundle-upsell] combo add failed', e);
            this.showAlert((this.config.labels && this.config.labels.errorGeneric) || 'Error');
          } finally {
            if (submitBtn) { submitBtn.classList.remove('loading'); submitBtn.removeAttribute('aria-disabled'); const sp = submitBtn.querySelector('.loading__spinner'); if (sp) sp.classList.add('hidden'); }
          }
          return;
        }

        // Companion-page swap: the customer is viewing the full-price companion (e.g. $58 Pod).
        // Once they've chosen the anchor (combo is valid), add the UNLISTED net variant ($20)
        // instead of the public one — so the cart holds the discounted Pod directly, no code.
        let mainIdToAdd = mainVariantId;
        if (this.isComboMode && this.config.currentIsCompanion && this.isAnchorInCart()) {
          const net = this.config.mainNetMap && this.config.mainNetMap[mainVariantId];
          if (net && net.id) mainIdToAdd = net.id;
        }

        // Tag each line item with _bundle_role so bundle-checkout.js can re-derive
        // tier from cart contents if cart attribute bundle_discount_pct is ever lost
        // (e.g., during the rebuild flow when admin disables then re-enables).
        // _-prefixed property is hidden from customer-facing displays (cart, checkout, receipt).
        const items = [
          { id: mainIdToAdd, quantity: mainQty, properties: { _bundle_role: 'main' } },
          ...selectedList.map((s) => ({
            id: s.variantId,
            quantity: 1,
            properties: { _bundle_role: 'accessory' },
          })),
        ];

        const submitBtn = this.productForm.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
          const spinner = submitBtn.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Always request cart-icon-bubble so the header count updates even if
        // cart.renderContents has issues. Merge with whatever cart needs.
        const baseSections = ['cart-icon-bubble'];
        const cartSections =
          cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map((s) => s.id)
            : [];
        const sectionsToFetch = Array.from(new Set([...baseSections, ...cartSections]));

        const payload = { items };
        if (sectionsToFetch.length) {
          payload.sections = sectionsToFetch.join(',');
          payload.sections_url = window.location.pathname;
        }

        try {
          const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
          console.log('[bundle-upsell] adding items', { url: `${url}.js`, payload });

          const res = await fetch(`${url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });

          let data = {};
          try {
            data = await res.json();
          } catch (parseErr) {
            console.error('[bundle-upsell] response not JSON', parseErr);
          }
          console.log('[bundle-upsell] cart/add response', { status: res.status, ok: res.ok, data });

          if (!res.ok || data.status) {
            const msg =
              data.description ||
              data.message ||
              (data.errors && (typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors))) ||
              `HTTP ${res.status}`;
            console.error('[bundle-upsell] add failed', msg, data);
            this.showAlert(msg);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bundle-upsell',
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            return;
          }

          // ===== Success path =====
          // Four modes (priority order):
          //   0. Combo Set (isComboMode=true) — multiple Shopify Automatic discounts (one
          //      per companion) apply server-side based on cart contents. Skip /discount,
          //      mark cart attribute with _COMBO_ sentinel for bundle-checkout.js.
          //   1. Automatic campaign (useAutomaticDiscount=true) — Shopify Automatic discount
          //      handles application based on cart contents. We skip /discount call, but write
          //      a sentinel cart attribute to suppress HBUNDLE fallback.
          //   2. Code campaign (overrideCode set) — apply that specific code via /discount URL.
          //   3. Normal HBUNDLE tier — count total accessories, apply HBUNDLE{tier}.
          const overrideCode = this.config.overrideCode;
          const useAutomatic = !!this.config.useAutomaticDiscount && this.config.enabled !== false;
          const isCodeCampaign = !!overrideCode && this.config.enabled !== false;

          let codeToApply = null;
          let tierPct = 0;
          let overrideAttr = '';
          if (this.isComboMode) {
            // Net-price architecture: companions are added as their UNLISTED net-price
            // variants, so the price IS the discount. No discount code, no /checkout warmup.
            // Just mark the cart so bundle-checkout.js skips HBUNDLE/?discount= routing.
            overrideAttr = '_COMBO_';
          } else if (useAutomatic) {
            // Don't apply any code — Shopify Automatic does it. Mark cart attribute so
            // bundle-checkout.js knows to skip HBUNDLE/?discount= routing too.
            overrideAttr = '_AUTO_';
          } else if (isCodeCampaign) {
            codeToApply = overrideCode;
            overrideAttr = overrideCode;
          } else {
            tierPct = await this.deriveCartBundleTier();
            if (tierPct > 0 && this.config.enabled !== false) {
              codeToApply = 'HBUNDLE' + tierPct;
            }
          }

          // Run saveBundleTier + saveBundleOverride + discount apply in PARALLEL.
          const tierJobs = [
            this.saveBundleTier(tierPct),
            this.saveBundleOverrideCode(overrideAttr),
          ];
          if (codeToApply) {
            tierJobs.push(
              fetch('/discount/' + encodeURIComponent(codeToApply), { credentials: 'same-origin' }).catch((discountErr) => {
                console.warn('[bundle-upsell] silent discount apply failed', discountErr);
              })
            );
          }
          await Promise.all(tierJobs);

          // 1) Manually update cart-icon-bubble (header count) FIRST,
          //    so it always refreshes regardless of what cart.renderContents does.
          try {
            this.replaceCartIconBubble(data);
          } catch (bubbleErr) {
            console.warn('[bundle-upsell] cart-icon-bubble update failed', bubbleErr);
          }

          // 2) Let cart drawer/notification re-render itself.
          if (cart && typeof cart.renderContents === 'function') {
            try {
              // CartNotification needs `key` to render the product slot; multi-item adds
              // return `items[]` instead of a top-level `key`, so fill it in.
              if (!data.key && data.items && data.items.length > 0) {
                data.key = data.items[0].key;
              }
              cart.renderContents(data);
            } catch (renderErr) {
              console.warn('[bundle-upsell] cart.renderContents threw, cart still updated', renderErr);
            }
          }
          try {
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          } catch (_) {}

          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-upsell',
                cartData: data,
              });
            }
          } catch (pubErr) {
            console.warn('[bundle-upsell] publish threw', pubErr);
          }

          // Reset selections so they can re-add cleanly if they linger
          this.selections = {};
          this.update();
        } catch (err) {
          console.error('[bundle-upsell] add failed (network or parse)', err);
          this.showAlert((this.config.labels && this.config.labels.errorGeneric) || 'Error');
        } finally {
          if (submitBtn) {
            submitBtn.removeAttribute('aria-disabled');
            submitBtn.classList.remove('loading');
            const spinner = submitBtn.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          }
        }
      }

      replaceCartIconBubble(data) {
        // Shopify returns sections HTML keyed by section id. The cart-icon-bubble
        // section's HTML is the *inner* content (svg + .cart-count-bubble div).
        // In Hulubulu's theme the cart icon lives inside the header as
        // <a id="cart-icon-bubble">…inner…</a>, so we replace the inner content there.
        if (!data || !data.sections || typeof data.sections['cart-icon-bubble'] !== 'string') return;

        const sectionHtml = data.sections['cart-icon-bubble'];

        // The section response may be wrapped in <div class="shopify-section">…</div>
        // or be the raw inner content. Handle both.
        let innerHtml = sectionHtml;
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const wrapper = parsed.querySelector('.shopify-section');
        if (wrapper) {
          innerHtml = wrapper.innerHTML;
        } else if (parsed.body) {
          innerHtml = parsed.body.innerHTML;
        }

        // Primary target: the <a id="cart-icon-bubble"> in the header.
        const liveLink = document.querySelector('#cart-icon-bubble');
        if (liveLink) {
          liveLink.innerHTML = innerHtml;
          return;
        }

        // Fallback: a wrapper section with that id (some themes use this layout).
        const liveSection = document.querySelector('#shopify-section-cart-icon-bubble');
        if (liveSection) {
          liveSection.innerHTML = innerHtml;
        }
      }

      showAlert(text) {
        // Lightweight alert. Could be replaced with a styled toast.
        try {
          alert(text);
        } catch (e) {}
      }

      // ===== Bundle discount helpers =====
      // computeTierPct: based on the customer's CURRENT UI selections (used only for
      // preview rendering — stepper progress, summary discount line, etc.)
      // Returns 0 in campaign mode (override code) since tier ladder doesn't apply.
      computeTierPct() {
        if (this.config.enabled === false || this.config.discountType !== 'percentage') return 0;
        if (this.isComboMode || this.config.overrideCode || this.config.useAutomaticDiscount) return 0; // any campaign/combo mode bypasses tier
        const count = Object.keys(this.selections).length;
        if (count === 0 || !this.config.tiers) return 0;
        const sorted = this.config.tiers.slice().sort((a, b) => b.qty - a.qty);
        const tier = sorted.find((t) => count >= t.qty);
        return tier ? tier.value : 0;
      }

      // deriveCartBundleTier: based on TOTAL accessories already in cart (across all
      // bundle adds). Used after add-to-cart to apply the right discount code.
      // Counts line items tagged _bundle_role:accessory.
      async deriveCartBundleTier() {
        if (this.config.enabled === false || this.config.discountType !== 'percentage') return 0;
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          const accessoryCount = (cart.items || []).reduce((sum, i) => {
            const role = i.properties && i.properties._bundle_role;
            return role === 'accessory' ? sum + (i.quantity || 1) : sum;
          }, 0);
          if (!this.config.tiers || accessoryCount === 0) return 0;
          const sorted = this.config.tiers.slice().sort((a, b) => b.qty - a.qty);
          const tier = sorted.find((t) => accessoryCount >= t.qty);
          return tier ? tier.value : 0;
        } catch (e) {
          console.warn('[bundle-upsell] deriveCartBundleTier failed', e);
          return 0;
        }
      }

      async saveBundleOverrideCode(code) {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { bundle_override_code: code || '' } }),
          });
        } catch (e) {
          console.warn('[bundle-upsell] saveBundleOverrideCode failed', e);
        }
      }

      async saveBundleTier(pct) {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { bundle_discount_pct: pct > 0 ? String(pct) : '' } }),
          });
        } catch (e) {
          console.warn('[bundle-upsell] saveBundleTier failed', e);
        }
      }

      // ===== Stepper =====
      updateStepper(count) {
        const stepper = this.querySelector('[data-bundle-stepper]');
        if (!stepper) return;

        const nodes = Array.from(stepper.querySelectorAll('[data-tier-node]'));
        if (!nodes.length) return;

        const tierQtys = nodes.map((n) => parseInt(n.dataset.tierQty, 10) || 0);
        const maxQty = Math.max.apply(null, tierQtys);

        nodes.forEach((node, i) => {
          node.classList.remove('is-unlocked', 'is-current');
          if (count >= tierQtys[i]) {
            node.classList.add('is-unlocked');
          }
        });

        const currentIdx = tierQtys.findIndex((q) => count < q);
        if (currentIdx >= 0) {
          nodes[currentIdx].classList.add('is-current');
        }

        const progressEl = stepper.querySelector('[data-bundle-stepper-progress]');
        if (progressEl) {
          const firstQty = tierQtys[0];
          const span = maxQty - firstQty;
          let pctFill;
          if (span <= 0) {
            pctFill = count >= firstQty ? 100 : 0;
          } else {
            pctFill = ((count - firstQty) / span) * 100;
            pctFill = Math.max(0, Math.min(100, pctFill));
          }
          const ratio = pctFill / 100;
          progressEl.style.setProperty('transform', 'scaleX(' + ratio + ')', 'important');
        }
      }

      // ===== Per-pick price rendering =====
      currentTierPct(count) {
        if (this.config.enabled === false || this.config.discountType !== 'percentage') return 0;
        if (this.isComboMode) return 0; // combo savings come from per-pick metafield, not tier %
        if (!count || !this.config.tiers) return 0;
        const sorted = this.config.tiers.slice().sort((a, b) => b.qty - a.qty);
        const tier = sorted.find((t) => count >= t.qty);
        return tier ? tier.value : 0;
      }

      renderPickPrice(priceEl, accPrice, savings, opts) {
        if (!priceEl) return;
        const options = opts || {};
        // Guard: cap savings so discounted price never goes below 0 (extreme edge case)
        const cappedSavings = Math.max(0, Math.min(savings || 0, accPrice));
        if (cappedSavings > 0) {
          const discounted = accPrice - cappedSavings;
          const labels = this.config.labels || {};
          const saveTpl = labels.savingsLabel || 'Save [amount]';
          const saveText = saveTpl.replace('[amount]', this.formatMoney(cappedSavings));
          const savingHtml = options.skipSavingLine
            ? ''
            : `<span class="bundle-pick__price-savings">${this.escapeHtml(saveText)}</span>`;
          priceEl.innerHTML =
            `<s class="bundle-pick__price-original">${this.escapeHtml(this.formatMoney(accPrice))}</s>` +
            ` <span class="bundle-pick__price-discounted">${this.escapeHtml(this.formatMoney(discounted))}</span>` +
            savingHtml;
        } else {
          priceEl.textContent = this.formatMoney(accPrice);
        }
      }

      resetPickPrice(priceEl) {
        if (!priceEl) return;
        priceEl.textContent = priceEl.dataset.defaultPrice || '';
      }

      // ===== Helpers =====
      escapeHtml(s) {
        return String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }

      formatMoney(cents) {
        const format = this.config.moneyFormat || '${{amount}}';
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const key = placeholder ? placeholder[1] : 'amount';
        const num = (cents || 0) / 100;
        let str;
        switch (key) {
          case 'amount_no_decimals':
          case 'amount_no_decimals_with_comma_separator':
            str = Math.round(num).toLocaleString('en-US');
            break;
          case 'amount_with_comma_separator':
            str = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            break;
          default:
            str = num.toFixed(2);
        }
        return format.replace(/\{\{\s*\w+\s*\}\}/, str);
      }
    }
  );
}
